import React, { useEffect, useMemo, useRef, useState } from "react";
import { Fields } from "uicomponents";
import useTranslation from "hooks/useTranslation";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import useAlert from "hooks/useAlert";
import ModulesActions from "modules/entity/actions";
import get from "lodash/get";

const OTP_REQUEST_URL = `${import.meta.env.VITE_API_ROOT}/auth/send-confirmation-code?&_f=json`;

const ITEMS = [
	{ key: "consumptions", label: "Расход товаров" },
	{ key: "orders", label: "Заказы" },
	{ key: "comings", label: "Приход товаров" },
	{ key: "transfers", label: "Перемещение товаров" },
	{ key: "out_comings", label: "Возврат товаров" },
	{ key: "adjustments", label: "Инвентаризация товаров" },
	{ key: "storage_items", label: "Остаток товаров" },
	{ key: "transactions", label: "Транзакции" },
	{ key: "logs", label: "Все отчеты" }
];

const MasterCheckbox = ({ list, setList, label }) => {
	const total = ITEMS.length;
	const checkedCount = list.length;
	const all = checkedCount === total;
	const some = checkedCount > 0 && checkedCount < total;

	const ref = useRef(null);
	useEffect(() => {
		if (ref.current) ref.current.indeterminate = some && !all;
	}, [some, all]);

	const toggleAll = () => {
		setList(all ? [] : ITEMS.map(i => i.key));
	};

	return (
		<label className="d-flex align-items-center mb-10" style={{ gap: 10 }}>
			<input
				ref={ref}
				type="checkbox"
				className="style-checkbox"
				checked={all}
				onChange={toggleAll}
			/>
			<span className="fw-600 lh-24">{label}</span>
		</label>
	);
};

const DeleteReports = ({ showClearDataModal }) => {
	const { showAlert } = useAlert();
	const dispatch = useDispatch();

	const { tr } = useTranslation();
	const profile = useSelector(state => state.auth.user);

	const CLEAR_ITEMS = useMemo(
		() => ITEMS.map(i => ({ key: i.key, label: tr(i.label) })),
		[tr]
	);

	const [otpOpen, setOtpOpen] = useState(false);
	const [otpValue, setOtpValue] = useState("");
	const [otpRequesting, setOtpRequesting] = useState(false);
	const [otpSubmitting, setOtpSubmitting] = useState(false);
	const [selectedTables, setSelectedTables] = useState([]);

	const requestOtp = async tables => {
		if (!tables || !tables.length) return;
		setOtpRequesting(true);
		try {
			const res = await fetch(OTP_REQUEST_URL, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ phone: profile.phone })
			});

			let data = null;
			try {
				data = await res.json();
			} catch (e) {
				data = null;
			}

			let receivedCode = "";
			if (data) {
				if (data.code) receivedCode = String(data.code);
				else if (data.data && data.data.code)
					receivedCode = String(data.data.code);
				else if (data.result && data.result.code)
					receivedCode = String(data.result.code);
			}

			setOtpValue(receivedCode);
			setOtpOpen(true);
		} catch (e) {
			showAlert(tr("Не удалось отправить код"), { type: "danger" });
		} finally {
			setOtpRequesting(false);
		}
	};

	const submitClearData = code => {
		return new Promise(resolve => {
			dispatch(
				ModulesActions.FormDefault.request({
					method: "post",
					url: `/user/organization/${profile.organization_id}/clear-data`,
					values: {
						code,
						tables: selectedTables
					},
					cb: {
						success: data => {
							setOtpOpen(false);
							setOtpValue("");
							setSelectedTables([]);
							showClearDataModal(false);
							showAlert(get(data, "message", tr("Успешно")), { type: "success" });
							resolve();
						},
						error: error => {
							const msg =
								get(error, "message") ||
								get(error, "errorMessage") ||
								tr("Что-то пошло не так");
							showAlert(msg, { type: "danger" });
							resolve();
						},
						finally: () => {
							setOtpSubmitting(false);
						}
					}
				})
			);
		});
	};

	const toggleOne = key => {
		const has = selectedTables.indexOf(key) !== -1;
		const next = has
			? selectedTables.filter(k => k !== key)
			: selectedTables.concat(key);
		setSelectedTables(next);
	};

	const onPrimaryClick = e => {
		e.preventDefault();
		if (!selectedTables.length) {
			showAlert(tr("Выберите хотя бы один пункт"), { type: "warning" });
			return;
		}
		requestOtp(selectedTables);
	};

	const confirmOtpAndSubmit = async e => {
		if (e && typeof e.preventDefault === "function") e.preventDefault();
		if (e && typeof e.stopPropagation === "function") e.stopPropagation();

		const nextCode = (otpValue || "").trim();
		if (!nextCode) {
			showAlert(tr("Введите код"), { type: "warning" });
			return;
		}

		if (!selectedTables.length) {
			showAlert(tr("Выберите хотя бы один пункт"), { type: "warning" });
			return;
		}

		setOtpSubmitting(true);
		await submitClearData(nextCode);
	};

	return (
		<div className="reports-modal mt-10">
			<MasterCheckbox
				list={selectedTables}
				setList={setSelectedTables}
				label={tr("Будут очищены:")}
			/>

			<div className="checkbox-list" style={{ display: "grid", gap: 8 }}>
				{CLEAR_ITEMS.map(item => {
					const checked = selectedTables.indexOf(item.key) !== -1;
					return (
						<label
							key={item.key}
							className="d-flex align-items-center"
							style={{ gap: 10, padding: "6px 8px", borderRadius: 10 }}
						>
							<input
								type="checkbox"
								className="style-checkbox"
								checked={checked}
								onChange={() => toggleOne(item.key)}
							/>
							<span>- {item.label}</span>
						</label>
					);
				})}
			</div>

			<button
				type="button"
				disabled={!selectedTables.length || otpRequesting || otpSubmitting}
				onClick={onPrimaryClick}
				className={
					"d-btn d-btn-primary d-btn--small mt-20" +
					(otpRequesting ? " --loading" : "")
				}
			>
				{tr("Очистить все отчеты")}
			</button>

			<Modal
				open={otpOpen}
				onCancel={() => setOtpOpen(false)}
				footer={null}
				centered
				title={tr("Введите код подтверждения")}
			>
				<div className="mb-10">
					<Fields.InputS1Default
						value={otpValue}
						onChange={e => setOtpValue(e.target.value)}
						placeholder={tr("Код из SMS/приложения")}
						autoFocus
						size="small"
						className="mb-0"
					/>
				</div>
				<div
					className="d-flex"
					style={{ gap: 8, justifyContent: "flex-end" }}
				>
					<button
						type="button"
						className="d-btn d-btn-light d-btn--small"
						onClick={() => setOtpOpen(false)}
					>
						{tr("Отмена")}
					</button>
					<button
						type="button"
						className={"d-btn d-btn-primary d-btn--small"}
						disabled={otpSubmitting || otpRequesting}
						onClick={confirmOtpAndSubmit}
					>
						{tr("Подтвердить")}
					</button>
				</div>
			</Modal>
		</div>
	);
};

export default DeleteReports;
